import { useEffect, useState } from 'react';
import {
  reject, uniq,
} from 'lodash';
import Router from 'next/router';
import {
  Post, PostMetricsUpdate, PostScore,
} from 'lib/types/posts';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { NETWORKS, NETWORK_IDS, POST_TYPES } from 'lib/constants';
import { truncate, userIsAdmin } from 'lib/helpers';
import { A } from 'components/A';
import { DeletePostModal } from 'components/DeletePostModal';
import { Feedback } from 'components/Feedback';
import { UpdatePostMetricsModal } from 'components/UpdatePostMetricsModal';
import { PostAccount } from 'components/PostAccount';
import { PostCaption } from 'components/PostCaption';
import { PostMetrics } from 'components/PostMetrics';
import { useSnackbar } from 'notistack';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { ShareButton } from 'components/ShareButton';
import StarIcon from '@mui/icons-material/Star';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { MobileTooltip } from 'components/MobileTooltip';
import { trackClickAddFavoritePost, trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { useSignUpModal } from 'components/SignUpModal';
import { useModal } from 'mui-modal-provider';
import Link from 'next/link';
import { PostMenu } from './PostMenu';
import { AdminPostMenu } from './AdminPostMenu';

interface Props {
  post: Post;
  onChange: () => void;
  onScrapePost: () => void;
  isScraping: boolean;
  postScore?: PostScore;
}

export const PostInfo = ({
  post, onChange, onScrapePost, isScraping, postScore,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { showModal } = useModal();

  const [openUpdateMetrics, setOpenUpdateMetrics] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const networkName = NETWORKS[post.network].title;
  const [postedAtDate, setPostedAtDate] = useState<string | null>(null);
  const { showSignUpModal } = useSignUpModal();

  useEffect(() => {
    setPostedAtDate(new Date(post.postedAt).toLocaleString());
  }, [post.postedAt, setPostedAtDate]);

  const deletePost = async () => {
    await API.deletePost(post.id);
    Router.push('/');
  };

  const updateMetrics = async (postMetrics: PostMetricsUpdate) => {
    setOpenUpdateMetrics(false);
    await API.updatePostMetricsAndPost(post.id, postMetrics);
    onChange();
    enqueueSnackbar('Metrics updated', {
      variant: 'info',
    });
  };

  const renderShareText = () => {
    const baseText = `Check out this @${post.account} post on Gondola!`;
    const allHandles = post.credits
      .filter((c) => !!c.user)
      .map((c) => `@${c.user?.username}`);
    const uniqueHandles = uniq(reject(allHandles, (h) => h === `@${post.account}`));
    if (!uniqueHandles.length) {
      return baseText;
    }
    return `${baseText} (Credits: ${uniqueHandles.join(', ')})`;
  };

  const [isFavorited, setIsFavorited] = useState(post.isFavorited);
  const handleFavoritePost = async () => {
    if (!isFavorited) {
      await trackClickAddFavoritePost(API, post.id, 'post-detail');
    }

    // todo: move to helper or hook
    if (!post.favoritesListId) {
      trackClickSignupEvent(API, 'favorite-post');
      showSignUpModal('Please sign in to favorite posts');
      return;
    }
    try {
      if (isFavorited) {
        await API.removeItemFromList(post.favoritesListId, post.id);
      } else {
        await API.addPostToList(post.favoritesListId, post.id);
      }
      setIsFavorited(!isFavorited);
    } catch (err) {
      enqueueSnackbar('An error occurred while trying to favorite this post');
    }
  };

  const handleFeedbackModal = () => {
    const modal = showModal(Feedback, {
      onClose: () => {
        modal.destroy();
      },
      title: 'Report a Bug',
      text: 'Add a brief description of the bug or platform issue you are experiencing, and how we can reproduce it.',
    });
  };

  const handleDeletePostModal = () => {
    const modal = showModal(DeletePostModal, {
      onClose: () => {
        modal.destroy();
      },
      deletePost,
    });
  };

  const renderProvisionalStatsText = () => {
    if (post.network === NETWORK_IDS.instagram
      && post.type === POST_TYPES.VIDEO
      && !post.currentPlays
      && !post.currentViews) {
      return 'Not seeing views yet? Check back later!';
    }

    if (post.provisional && post.network === NETWORK_IDS.facebook) {
      return (
        <>
          If you are missing stats on this Facebook post,
          {' '}
          <Link href="/settings/accounts" className="underline">
            connect the Facebook page
          </Link>
        </>
      );
    }

    if (post.provisional) {
      return 'Not seeing some stats? Check back later, or upgrade to PRO to refresh now!';
    }

    return null;
  };

  return (
    <>
      {post.deleted && (
        <div className="w-full bg-paleyellow p-2">
          This post has been deleted. Only admin can view this page.
        </div>
      )}
      <div className="flex flex-wrap justify-between items-center py-4">
        <PostAccount
          linkToProfile
          size="large"
          post={post}
        />
        <div className="flex justify-end items-center">
          <MobileTooltip title={`${isFavorited ? 'Remove post from' : 'Add post to'} favorites`}>
            <button type="button" aria-label="favorite-post" onClick={handleFavoritePost}>
              {isFavorited ? <StarIcon color="primary" fontSize="large" /> : <StarBorderOutlinedIcon color="primary" fontSize="large" />}
            </button>
          </MobileTooltip>
          <div className="ml-2">
            <ShareButton text={renderShareText()} />
          </div>
          <PostMenu
            post={post}
            onChange={onChange}
            isScraping={isScraping}
            onScrapePost={onScrapePost}
            onOpenDelete={handleDeletePostModal}
            onOpenFeedback={handleFeedbackModal}
          />
          {userIsAdmin(currentUser) && (
            <AdminPostMenu
              post={post}
              onChange={onChange}
              onOpenUpdateMetrics={() => setOpenUpdateMetrics(true)}
            />
          )}
        </div>
      </div>
      <PostCaption networkId={post.network}>
        {truncate(post.caption, 1000)}
      </PostCaption>
      {post.isLive && !!postedAtDate && post.type !== POST_TYPES.STORY ? (
        <div className="font-light pt-4">
          <A target="_blank" href={post.url}>
            Posted on
            {' '}
            {networkName}
            {' '}
            at
            {' '}
            {postedAtDate}
            {' '}
            <OpenInNew fontSize="small" />
          </A>
        </div>
      )
        : (!!postedAtDate
          && (
            <div className="font-light pt-4">
              Posted on
              {' '}
              {postedAtDate}
            </div>
          )
        )}
      <div className="pb-1 border-b">
        <PostMetrics
          post={post}
          decimals={1}
          isScraping={isScraping}
          onScrapePost={onScrapePost}
          postScore={postScore}
          showSparklines
        />
        <div className="text-sm text-gray-600 dark:text-gray-400 text-center">{renderProvisionalStatsText()}</div>
        <UpdatePostMetricsModal
          isOpen={openUpdateMetrics}
          updateMetrics={updateMetrics}
          onRequestClose={() => setOpenUpdateMetrics(false)}
          post={post}
        />
      </div>
    </>
  );
};
